import {Injectable, OnDestroy} from "@angular/core";
import {from, Observable, of, Subject} from "rxjs";
import {httpParamSerializer} from "../lib/utils";
import {
    ComponentConstantField,
    ConstantRelationshipAggregationRequest,
    ConstantValue,
    GenericConstantApiResponse, GenericConstantRelationshipRequest
} from '../_models/api/generic-constant';
import {IDMap, ModelAttribute, ModelID} from '../_typing/generic-types';
import {HttpClient} from '@angular/common/http';
import {last, mergeMap} from "rxjs/operators";
import {unique} from "jquery";
import {set} from "lodash-es";

/**
 * This class is for shared functions relating to data access on the component_type api
 **/
@Injectable({
    providedIn: 'root'
})
export class GenericConstantDataService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();

    constructor(private http: HttpClient) {
    }

    saveComponentConstants(component_constants: ComponentConstantField[]) {
        const concurrentRequests = 10;
        const $constants = [];
        component_constants.forEach(cc => {
            if (typeof cc.value === 'string') {
                cc.value = cc.value.trim();
            }
            $constants.push(this.saveComponentConstant(cc.component_id, cc.constant_property_id, cc.value));
        });
        return from($constants).pipe(
            mergeMap(obj => obj, concurrentRequests),
            last());
    }

    saveComponentConstant(component_id: ModelID, property_id: ModelID, value: ConstantValue): Observable<GenericConstantApiResponse> {
        const params = {
            'component_id': component_id,
            'constant_property_id': property_id,
            'value': value
        };
        return this.http.post('/api/constant/component', params);
    }

    saveEventConstant(event_id: ModelID, property_id: ModelID, value: ConstantValue): Observable<GenericConstantApiResponse> {
        const params = {
            'event_id': event_id,
            'constant_property_id': property_id,
            'value': value
        };
        return this.http.post('/api/constant/event', params);
    }

    getComponentConstants(component_ids: ModelID[], constant_properties_ids: ModelID[], attributes?: ModelAttribute[], relationshipMap?: IDMap<ModelID[]>, relationshipAttributeMap?: IDMap<string[]>, aggregations?: ConstantRelationshipAggregationRequest[], showAggregations?: boolean, componentTypeRelationships?: IDMap<any>): Observable<GenericConstantApiResponse> {
        if (!component_ids.length || (constant_properties_ids.length <= 0 && attributes && attributes.length <= 0)) {
            return of({data: {}});
        }
        const params = {
            'component_ids': component_ids,
        };
        if (constant_properties_ids?.length) {
            params['constant_property_ids'] = constant_properties_ids;
        }
        if (attributes?.length) {
            params['attributes'] = attributes;
        }
        if (aggregations?.length && showAggregations) {
            // Filter aggregations to mark entity aggregations correctly
            params['aggregation'] = aggregations.map(agg => {
                // Check if this is a component type aggregation
                if (componentTypeRelationships && componentTypeRelationships[agg.property_id]) {
                    return {...agg, aggregation_target: 'entity'};
                }
                return agg;
            });
        }

        if ((relationshipMap && Object.keys(relationshipMap)?.length) ||
            (relationshipAttributeMap && Object.keys(relationshipAttributeMap)?.length) ||
            (componentTypeRelationships && Object.keys(componentTypeRelationships)?.length)) {
            let relationshipParams: any = [];

            // Handle property relationships
            const keys = (Object.keys(relationshipMap) || []).concat(Object.keys(relationshipAttributeMap) || []);
            [...new Set(keys)].forEach(componentTypeId => {
                let rel: GenericConstantRelationshipRequest = {
                    type: 'component',
                    type_id: componentTypeId
                }
                if (relationshipMap[componentTypeId]) {
                    rel.constant_property_ids = relationshipMap[componentTypeId]
                }
                if (relationshipAttributeMap[componentTypeId]) {
                    rel.attributes = relationshipAttributeMap[componentTypeId]
                }
                if (aggregations?.length) {
                    // Only include aggregations that belong to this specific relationship
                    rel.aggregation = aggregations.filter(agg => agg.type_id === componentTypeId);
                }
                relationshipParams.push(rel);
            })

            // Handle component type relationships
            if (componentTypeRelationships) {
                Object.keys(componentTypeRelationships).forEach(componentTypeId => {
                    const relConfig = componentTypeRelationships[componentTypeId];
                    let rel: any = {
                        type: 'component',
                        type_id: componentTypeId,
                        constant_property_ids: [],
                        attributes: ['name']  // Always fetch name for display
                    };

                    // Add aggregation for counting if needed
                    if (relConfig.aggregation_type === 'count' || relConfig.aggregation_type === 'default') {
                        rel.aggregation = [{
                            property_id: componentTypeId,
                            method: 'count',
                            type_id: componentTypeId,
                            type: 'component',
                            aggregation_target: 'entity'
                        }];
                    }

                    relationshipParams.push(rel);
                });
            }

            params['relationships'] = relationshipParams;
        }

        return this.http.post('/api/constant/component/query', params);
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
